import { useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminFetch } from "@/utils/adminFetch";
import ImagePicker from "@/components/ImagePicker";

const DESTINATIONS = ["naples", "pozzuoli", "bacoli", "sorrento", "pompeii", "capri", "procida", "ischia", "amalfi-coast", "caserta", "apennines"];

function LangField({ label, valueIt, valueEn, onChangeIt, onChangeEn, textarea, testid }) {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{label} · IT</label>
        <Cmp
          value={valueIt}
          onChange={(e) => onChangeIt(e.target.value)}
          rows={textarea ? 5 : undefined}
          data-testid={`${testid}-it`}
          className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{label} · EN</label>
        <Cmp
          value={valueEn}
          onChange={(e) => onChangeEn(e.target.value)}
          rows={textarea ? 5 : undefined}
          data-testid={`${testid}-en`}
          className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
        />
      </div>
    </div>
  );
}

export default function AdminExperienceEditor({ exp, onClose, onSaved }) {
  const isNew = !exp.slug;
  const [form, setForm] = useState({
    slug: exp.slug || "",
    destination: exp.destination || "pozzuoli",
    categories: exp.categories || ["culture"],
    title: { ...exp.title },
    short: { ...(exp.short || {}) },
    description: { ...(exp.description || {}) },
    duration: { ...(typeof exp.duration === "object" ? exp.duration : {}) },
    group_size: { ...(exp.group_size || {}) },
    price_from: exp.price_from ?? 0,
    featured: !!exp.featured,
    image: exp.image || "/images/pozzuoli-coast.jpeg",
    gallery: [...(exp.gallery || [])],
    chapters: (exp.chapters || []).map((c) => ({
      kicker: { ...(c.kicker || {}) },
      title: { ...(c.title || {}) },
      text: { ...(c.text || {}) },
    })),
  });
  const [picker, setPicker] = useState(null); // 'image' | 'gallery'
  const [saving, setSaving] = useState(false);

  const setLoc = (field, lang, value) => setForm((f) => ({ ...f, [field]: { ...f[field], [lang]: value } }));
  const setChapter = (i, field, lang, value) =>
    setForm((f) => {
      const chapters = [...f.chapters];
      chapters[i] = { ...chapters[i], [field]: { ...chapters[i][field], [lang]: value } };
      return { ...f, chapters };
    });

  const save = async () => {
    if (isNew && !form.slug.trim()) {
      toast.error("Inserisci lo slug (es. mio-nuovo-tour)");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const doc = await adminFetch("/admin/experiences", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        toast.success("Pacchetto creato");
        onSaved(doc);
      } else {
        await adminFetch(`/admin/experiences/${exp.slug}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        toast.success("Esperienza aggiornata");
        onSaved({ ...exp, ...form });
      }
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-10" onClick={onClose} data-testid="admin-exp-editor">
      <div className="bg-ivory max-w-3xl w-full border border-stone2/60 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone2/50 sticky top-0 bg-ivory z-10">
          <p className="font-serif text-xl text-ink" data-testid="admin-exp-editor-title">{isNew ? "Nuovo pacchetto" : exp.title.it}</p>
          <button type="button" onClick={onClose} aria-label="Chiudi" data-testid="admin-exp-close" className="text-ink/50 hover:text-ink transition-colors">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {isNew && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">Slug (URL) *</label>
                <input
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  placeholder="es. tour-capri-al-tramonto"
                  data-testid="admin-exp-slug"
                  className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">Luogo</label>
                <select
                  value={form.destination}
                  onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))}
                  data-testid="admin-exp-destination"
                  className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
                >
                  {DESTINATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
          )}

          <LangField label="Titolo" valueIt={form.title.it || ""} valueEn={form.title.en || ""} onChangeIt={(v) => setLoc("title", "it", v)} onChangeEn={(v) => setLoc("title", "en", v)} testid="admin-exp-title" />
          <LangField label="Descrizione breve" valueIt={form.short.it || ""} valueEn={form.short.en || ""} onChangeIt={(v) => setLoc("short", "it", v)} onChangeEn={(v) => setLoc("short", "en", v)} textarea testid="admin-exp-short" />
          <LangField label="Descrizione" valueIt={form.description.it || ""} valueEn={form.description.en || ""} onChangeIt={(v) => setLoc("description", "it", v)} onChangeEn={(v) => setLoc("description", "en", v)} textarea testid="admin-exp-description" />
          <LangField label="Durata" valueIt={form.duration.it || ""} valueEn={form.duration.en || ""} onChangeIt={(v) => setLoc("duration", "it", v)} onChangeEn={(v) => setLoc("duration", "en", v)} testid="admin-exp-duration" />
          <LangField label="Gruppo" valueIt={form.group_size.it || ""} valueEn={form.group_size.en || ""} onChangeIt={(v) => setLoc("group_size", "it", v)} onChangeEn={(v) => setLoc("group_size", "en", v)} testid="admin-exp-group" />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">Prezzo base (€) — uso interno</label>
              <input
                type="number"
                value={form.price_from ?? 0}
                onChange={(e) => setForm((f) => ({ ...f, price_from: Number(e.target.value) }))}
                data-testid="admin-exp-price"
                className="mt-1.5 w-full border border-stone2 bg-ivory px-4 py-3 text-sm text-ink focus:outline-none focus:border-medblue"
              />
            </div>
            <label className="flex items-end gap-2 pb-3 text-sm text-ink/70 cursor-pointer">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                data-testid="admin-exp-featured"
                className="w-4 h-4 accent-[#1D4E89]"
              />
              In evidenza in homepage
            </label>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">Foto principale</label>
            <div className="mt-2 flex items-center gap-4">
              <img src={form.image} alt="" className="w-36 h-24 object-cover border border-stone2/50" data-testid="admin-exp-image-preview" />
              <button
                type="button"
                onClick={() => setPicker("image")}
                data-testid="admin-exp-image-change"
                className="border border-ink text-ink text-[10px] font-medium uppercase tracking-[0.15em] px-4 py-2.5 hover:bg-ink hover:text-ivory transition-colors duration-300"
              >
                Cambia foto
              </button>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">Galleria ({form.gallery.length} foto)</label>
            <div className="mt-2 flex flex-wrap gap-3">
              {form.gallery.map((g, i) => (
                <div key={i} className="relative group" data-testid={`admin-exp-gallery-item-${i}`}>
                  <img src={g} alt="" className="w-24 h-16 object-cover border border-stone2/50" />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, x) => x !== i) }))}
                    aria-label="Rimuovi"
                    data-testid={`admin-exp-gallery-remove-${i}`}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 text-ivory rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setPicker("gallery")}
                data-testid="admin-exp-gallery-add"
                className="w-24 h-16 border border-dashed border-stone2 text-ink/40 hover:text-medblue hover:border-medblue flex items-center justify-center transition-colors duration-300"
              >
                <Plus size={18} strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {/* CHAPTERS */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-[0.2em] text-ink/40">Capitoli narrativi ({form.chapters.length})</label>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, chapters: [...f.chapters, { kicker: { it: "", en: "" }, title: { it: "", en: "" }, text: { it: "", en: "" } }] }))}
                data-testid="admin-exp-chapter-add"
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-medblue hover:text-ink transition-colors"
              >
                <Plus size={12} /> Aggiungi capitolo
              </button>
            </div>
            <div className="mt-3 space-y-4">
              {form.chapters.map((c, i) => (
                <div key={i} className="border border-stone2/60 p-4 space-y-3" data-testid={`admin-exp-chapter-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-ink/40">Capitolo {i + 1}</span>
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, chapters: f.chapters.filter((_, x) => x !== i) }))}
                      data-testid={`admin-exp-chapter-remove-${i}`}
                      className="text-red-600/70 hover:text-red-600 transition-colors"
                      aria-label="Rimuovi capitolo"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {["it", "en"].map((lng) => (
                    <div key={lng} className="grid grid-cols-2 gap-2">
                      <input
                        value={c.kicker[lng] || ""}
                        onChange={(e) => setChapter(i, "kicker", lng, e.target.value)}
                        placeholder={`Sopratitolo (${lng.toUpperCase()})`}
                        data-testid={`admin-exp-chapter-kicker-${lng}-${i}`}
                        className="border border-stone2 bg-ivory px-3 py-2 text-xs text-ink focus:outline-none focus:border-medblue"
                      />
                      <input
                        value={c.title[lng] || ""}
                        onChange={(e) => setChapter(i, "title", lng, e.target.value)}
                        placeholder={`Titolo (${lng.toUpperCase()})`}
                        data-testid={`admin-exp-chapter-title-${lng}-${i}`}
                        className="border border-stone2 bg-ivory px-3 py-2 text-xs text-ink focus:outline-none focus:border-medblue"
                      />
                      <textarea
                        value={c.text[lng] || ""}
                        onChange={(e) => setChapter(i, "text", lng, e.target.value)}
                        placeholder={`Testo (${lng.toUpperCase()})`}
                        rows={3}
                        data-testid={`admin-exp-chapter-text-${lng}-${i}`}
                        className="col-span-2 border border-stone2 bg-ivory px-3 py-2 text-xs text-ink focus:outline-none focus:border-medblue"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-stone2/50 sticky bottom-0 bg-ivory">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.15em] text-ink/50 hover:text-ink px-4 py-3 transition-colors duration-300">
            Annulla
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="admin-exp-save"
            className="bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-8 py-3.5 hover:bg-ink disabled:opacity-40 transition-colors duration-300"
          >
            {saving ? "..." : isNew ? "Crea pacchetto" : "Salva modifiche"}
          </button>
        </div>
      </div>

      {picker && (
        <ImagePicker
          onClose={() => setPicker(null)}
          onSelect={(src) => {
            if (picker === "image") setForm((f) => ({ ...f, image: src }));
            else setForm((f) => ({ ...f, gallery: [...f.gallery, src] }));
            setPicker(null);
          }}
        />
      )}
    </div>
  );
}
